import React, { useState, useEffect } from 'react';

const AskQuestionModal = ({ isOpen, onClose, onSubmit, botResponse, isDarkMode, prefillMessage }) => {
    const [question, setQuestion] = useState('');
    const [category, setCategory] = useState('support');
    const [priority, setPriority] = useState('medium');
    const [includeContext, setIncludeContext] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setQuestion(prefillMessage || '');
            setCategory('support');
            setPriority('medium');
            setIncludeContext(false);
            setIsSubmitting(false);
        }
    }, [isOpen, prefillMessage]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!question.trim()) return;

        setIsSubmitting(true);

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        onSubmit({
            question,
            category,
            priority,
            includeContext,
            botResponseId: botResponse?.id,
            botResponseContent: botResponse?.content,
            timestamp: new Date().toISOString()
        });

        setIsSubmitting(false);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div
                className={`w-full max-w-lg rounded-2xl shadow-2xl transform transition-all ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
                    }`}
            >
                {/* Header */}
                <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Ask a Question
                    </h3>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                            }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-6">
                        {/* Bot Response Preview */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Regarding Bot Response
                            </label>
                            <div className={`text-sm line-clamp-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {botResponse?.content || "No content selected"}
                            </div>
                        </div>

                        {/* Question Input */}
                        <div>
                            <label
                                htmlFor="question"
                                className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                            >
                                Your Question <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="question"
                                rows={4}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="Ask your question or share your doubt about this response..."
                                className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all resize-none ${isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                    }`}
                                required
                            />
                        </div>

                        {/* Category and Priority Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Category */}
                            <div>
                                <label
                                    htmlFor="category"
                                    className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                                >
                                    Category
                                </label>
                                <select
                                    id="category"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all ${isDarkMode
                                        ? 'bg-gray-800 border-gray-700 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                >
                                    <option value="support">Support</option>
                                    <option value="billing">Billing</option>
                                    <option value="technical">Technical</option>
                                    <option value="sales">Sales</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            {/* Priority */}
                            <div>
                                <label
                                    htmlFor="priority"
                                    className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                                >
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all ${isDarkMode
                                        ? 'bg-gray-800 border-gray-700 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>

                                </select>
                            </div>
                        </div>

                        {/* Context Checkbox */}
                        <div className="flex items-start gap-3">
                            <div className="flex items-center h-5">
                                <input
                                    id="includeContext"
                                    type="checkbox"
                                    checked={includeContext}
                                    onChange={(e) => setIncludeContext(e.target.checked)}
                                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                            </div>
                            <label htmlFor="includeContext" className="text-sm">
                                <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                    Send full conversation to admin
                                </span>
                                <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {includeContext
                                        ? "Full chat history will be shared for better context."
                                        : "Only this specific question and selected bot response will be shared."}
                                </p>
                            </label>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={`p-6 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'} bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl flex justify-end gap-3`}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${isDarkMode
                                ? 'text-gray-300 hover:bg-gray-700'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!question.trim() || isSubmitting}
                            className={`px-6 py-2.5 rounded-xl font-medium text-white shadow-lg shadow-cyan-500/20 transition-all flex items-center gap-2 ${!question.trim() || isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed shadow-none'
                                : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 hover:shadow-cyan-500/30'
                                }`}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send to Admin
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AskQuestionModal;
